import React from "react";
import styled, { css } from "styled-components";
import type { Alignment } from "@blueprintjs/core";

import { Classes } from "@blueprintjs/core";
import type { ComponentProps } from "widgets/BaseComponent";
import { LabelPosition } from "components/constants";
import type { TextSize } from "constants/WidgetConstants";

import {
  CheckboxLabel,
  StyledCheckbox,
} from "widgets/CheckboxWidget/component";
import type { OptionProps, SelectAllState } from "../constants";
import { SelectAllStates } from "../constants";
import LabelWithTooltip, {
  labelLayoutStyles,
  LABEL_CONTAINER_CLASS,
} from "widgets/components/LabelWithTooltip";
import type { ThemeProp } from "widgets/constants";
import { AlignWidgetTypes } from "widgets/constants";
import _ from "lodash";
import {Colors} from "../../../constants/Colors";
import {FontStyleTypes} from "constants/WidgetConstants";

export interface InputContainerProps {
  inline?: boolean;
  optionCount: number;
  valid?: boolean;
  optionAlignment?: string;
  isDynamicHeightEnabled?: boolean;
  isAutoLayout: boolean;
  it_color?: string;
  it_size?: string;
  it_style?: string;
}

const InputContainer = styled.div<ThemeProp & InputContainerProps>`
  display: ${({ inline }) => (inline ? "inline-flex" : "flex")};
  ${({ inline }) => `
    flex-direction: ${inline ? "row" : "column"};
    align-items:  "flex-start";
    ${inline && "flex-wrap: wrap"};
  `}
  justify-content: ${({ inline, optionAlignment, optionCount }) =>
    !!optionAlignment
      ? optionAlignment
      : optionCount > 1
      ? `space-between`
      : inline
      ? `flex-start`
      : `center`};
  width: 100%;
  flex-grow: 1;
  height: 100%;
  border: 1px solid transparent;

  .t--checkbox-widget-label {
    color: ${({it_color}) => {
      return (it_color ? `${it_color}` : "var(--wds-color-text)") + ' !important'
    }};
    font-size: ${({it_size}) => {
      return (it_size ? `${it_size}` : "14px") + ' !important'
    }};
    font-style: ${({it_style}) => {
      return (it_style?.includes(FontStyleTypes.ITALIC) ? "italic" : "") + ' !important'
    }};
    font-weight: ${({it_style}) => {
      return (it_style?.includes(FontStyleTypes.BOLD) ? "bold" : "") + ' !important'
    }};
  }

  ${({ isAutoLayout }) =>
    isAutoLayout &&
    css`
      min-width: 232px;
    `}

  .${Classes.CONTROL} {
    display: flex;
    align-items: center;
    margin-bottom: 0;
    min-height: 30px;

    .bp3-control-indicator {
      margin-top: 0 !important;
    }
  }
`;

export interface CheckboxGroupContainerProps {
  compactMode: boolean;
  labelPosition?: LabelPosition;
}

export const CheckboxGroupContainer = styled.div<CheckboxGroupContainerProps>`
  ${labelLayoutStyles}
  & .${LABEL_CONTAINER_CLASS} {
    align-self: center;
    ${({ labelPosition }) =>
      labelPosition === LabelPosition.Left && "min-height: 30px"};
  }
`;

export interface SelectAllProps {
  checked: boolean;
  disabled?: boolean;
  indeterminate?: boolean;
  inline?: boolean;
  onChange: React.FormEventHandler<HTMLInputElement>;
  accentColor: string;
  borderRadius: string;
  isRequired?: boolean;
}

function SelectAll(props: SelectAllProps) {
  const {
    accentColor,
    borderRadius,
    checked,
    disabled,
    indeterminate,
    inline,
    isRequired,
    onChange,
  } = props;
  return (
    <StyledCheckbox
      accentColor={accentColor}
      borderRadius={borderRadius}
      checked={checked}
      className="whitespace-nowrap"
      disabled={disabled}
      indeterminate={indeterminate}
      inline={inline}
      labelElement={
        <CheckboxLabel
          alignment={AlignWidgetTypes.LEFT}
          className="t--checkbox-widget-label"
          disabled={disabled}
          isRequired={isRequired}
        >
          全选
        </CheckboxLabel>
      }
      onChange={onChange}
    />
  );
}

export interface CheckboxGroupComponentProps extends ComponentProps {
  isDisabled: boolean;
  isDynamicHeightEnabled?: boolean;
  isInline: boolean;
  isSelectAll?: boolean;
  isRequired?: boolean;
  isValid?: boolean;
  onChange: (value: string) => React.FormEventHandler<HTMLInputElement>;
  onSelectAllChange: (
    state: SelectAllState,
  ) => React.FormEventHandler<HTMLInputElement>;
  options: OptionProps[];

  selectedValues: string[];
  optionAlignment?: string;
  compactMode: boolean;
  labelText?: string;
  labelPosition?: LabelPosition;
  labelAlignment?: Alignment;
  labelTextColor?: string;
  labelTextSize?: TextSize;
  labelStyle?: string;
  labelWidth?: number;
  labelTooltip?: string;
  accentColor: string;
  borderRadius: string;
  isAutoLayout: boolean;
}
function CheckboxGroupComponent(props: CheckboxGroupComponentProps) {
  const {
    accentColor,
    borderRadius,
    compactMode,
    isAutoLayout,
    isDisabled,
    isDynamicHeightEnabled,
    isInline,
    isSelectAll,
    isValid,
    labelAlignment,
    labelPosition,
    labelStyle,
    labelText,
    labelTextColor,
    labelTextSize,
    labelTooltip,
    labelWidth,
    onChange,
    onSelectAllChange,
    optionAlignment,
    options,
    selectedValues,
    widgetId,
    isRequired,
    it_color,
    it_size,
    it_style,
  } = props;

  const selectAllChecked = selectedValues.length === options.length;
  const selectAllIndeterminate =
    !selectAllChecked && selectedValues.length >= 1;
  const selectAllState = selectAllChecked
    ? SelectAllStates.CHECKED
    : selectAllIndeterminate
    ? SelectAllStates.INDETERMINATE
    : SelectAllStates.UNCHECKED;

  let optionCount = (options || []).length;
  if (isSelectAll) {
    optionCount += 1;
  }

  return (
    <CheckboxGroupContainer
      compactMode={compactMode}
      data-testid="checkboxgroup-container"
      labelPosition={labelPosition}
    >
      {labelText && (
        <LabelWithTooltip
          alignment={labelAlignment}
          className={`checkboxgroup-label`}
          color={labelTextColor}
          compact={compactMode}
          disabled={isDisabled}
          fontSize={labelTextSize}
          fontStyle={labelStyle}
          helpText={labelTooltip}
          inline={isInline}
          isDynamicHeightEnabled={isDynamicHeightEnabled}
          optionCount={optionCount}
          position={labelPosition}
          text={labelText}
          width={labelWidth}
          isRequired={isRequired}
        />
      )}
      <InputContainer
        data-testid="checkbox-group-container"
        inline={isInline}
        isAutoLayout={isAutoLayout}
        isDynamicHeightEnabled={isDynamicHeightEnabled}
        optionAlignment={optionAlignment}
        optionCount={options.length}
        it_color={it_color}
        it_size={it_size}
        it_style={it_style}
      >
        {isSelectAll && (
          <SelectAll
            accentColor={accentColor}
            borderRadius={borderRadius}
            checked={selectAllChecked}
            disabled={isDisabled}
            indeterminate={selectAllIndeterminate}
            inline={isInline}
            onChange={onSelectAllChange(selectAllState)}
          />
        )}
        {
          options&&options.length===0&&(
            <div style={{color:'#999'}}>无选项，请配置选项</div>
          )
        }
        {options &&
          options.length > 0 &&
          [...options].map((option: OptionProps, index: number) => (
            <StyledCheckbox
              accentColor={accentColor}
              borderRadius={borderRadius}
              checked={( _.isArray(selectedValues) ? selectedValues : []).includes(option.value)}
              disabled={isDisabled}
              hasError={!isValid}
              inline={isInline}
              key={`${widgetId}-checkbox-${index}`}
              labelElement={
                <CheckboxLabel
                  alignment={AlignWidgetTypes.LEFT}
                  className="t--checkbox-widget-label"
                  disabled={isDisabled}
                >
                  {option.label}
                </CheckboxLabel>
              }
              onChange={onChange(option.value)}
            />
          ))}
      </InputContainer>
    </CheckboxGroupContainer>
  );
}

export default CheckboxGroupComponent;
